import { Fragment, useMemo } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import { toBigInt, WeiPerEther, ZeroAddress } from "ethers";
import { useParams } from "react-router";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { StyledEmptyWrapper, StyledPagination } from "@framework/styled";
import { ILootBox } from "@framework/types";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";

import { LootBoxListItem } from "./item";
import { LootBoxSearchForm } from "./search";

export interface ILootboxListProps {
  embedded?: boolean;
}

export const LootBoxList = (props: ILootboxListProps) => {
  const { embedded } = props;

  const { id } = useParams<{ id: string }>();

  const initialSearchValues = useMemo(() => {
    return {
      contractIds: embedded ? [~~id!] : [],
      minPrice: ZeroAddress.toString(),
      maxPrice: (WeiPerEther * toBigInt(1000)).toString(),
    };
  }, [id]);

  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount } = useFetchQuery<
    IPaginationResult<ILootBox>
  >("/loot/boxes", { enableInfiniteScroll: isMd }, void 0, initialSearchValues);

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "loot", "loot.boxes"]} isHidden={embedded} />

      <PageHeader message="pages.loot.boxes.title" />

      <LootBoxSearchForm initialValues={initialSearchValues} embedded={embedded} />

      <ProgressOverlay isLoading={isLoading}>
        <Grid container spacing={2}>
          <StyledEmptyWrapper count={allRows.length} isLoading={isLoading}>
            {allRows.map(lootbox => (
              <Grid size={{ lg: 4, sm: 6, xs: 12 }} key={lootbox.id}>
                <LootBoxListItem lootBox={lootbox} />
              </Grid>
            ))}
          </StyledEmptyWrapper>
        </Grid>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}
    </Fragment>
  );
};
